import { Component, Input, OnInit, inject } from '@angular/core';
import { Capture } from 'app/core/models/capture.model';
import { DatePipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { CaptureUserAuditOutcomeStatusPipe } from '../../../shared/pipes/capture-user-audit-outcome-status.pipe';
import { CaptureUserAuditOutcomeStatusReasonsPipe } from '../../../shared/pipes/capture-user-audit-outcome-status-reasons.pipe';
import { AuthenticationService } from 'app/core/services/authentication.service';
import { UserRoleName } from 'app/core/enums/user-role-name.enum';
import { NgSelectComponent } from '@ng-select/ng-select';
import { outcomeStatusOptions } from 'app/core/options/capture-user-audit-outcome-status.opts';
import { outcomeStatusReasonOptions } from 'app/core/options/capture-user-audit-outcome-status-reason.opts';
import { CaptureUserAuditOutcomeStatus } from 'app/core/enums/capture-user-audit-outcome-status.enum';
import { isEmpty } from 'lodash-es';
import { CaptureService } from 'app/core/services/capture.service';

@Component({
    selector: 'app-capture-user-audit',
    templateUrl: './capture-user-audit.component.html',
    styleUrls: ['./capture-user-audit.component.scss'],
    providers: [],
    imports: [
      ReactiveFormsModule,
      FaIconComponent,
      DatePipe,
      CaptureUserAuditOutcomeStatusPipe,
      CaptureUserAuditOutcomeStatusReasonsPipe,
      NgSelectComponent]
})
export class CaptureUserAuditComponent implements OnInit {
  private authService = inject(AuthenticationService);
  private formBuilder = inject(UntypedFormBuilder);
  private captureService = inject(CaptureService);

  @Input() capture: Capture;

  displayForm = false;
  canCreate = false;
  canEdit = true;
  processing = false;
  serverErrorMessage: string;

  formGroup: FormGroup;

  captureUserAuditOutcomeStatus = CaptureUserAuditOutcomeStatus;
  availableOutcomes = outcomeStatusOptions;
  availableOutcomeStatusReasons = outcomeStatusReasonOptions;

  ngOnInit() {
    this.canCreate = this.authService.isCaptureAdminUser;
    this.canEdit = this.authService.isCaptureAdminUser &&
                   this.authService.hasPermissionTo(UserRoleName.editVerifiedCaptures);
    this.displayForm = this.canCreate && !this.capture.userAuditOutcome;
    this.buildFormGroup();
  }

  onEdit($event) {
    $event.preventDefault();
    this.displayForm = this.canEdit;
  }

  onSubmit() {
    const status = this.formGroup.get('status').value;
    const statusReasons = status !== this.captureUserAuditOutcomeStatus.compliant ? this.selectedOutcomeReasons : [];
    const note = this.formGroup.get('note').value;

    const params = {
      status,
      statusReasons,
      note
    };

    this.processing = true;
    this.serverErrorMessage = null;

    if (this.capture.userAuditOutcome) {
      this.handleUpdateAuditOutcome(params);
    } else {
      this.handleCreateAuditOutcome(params);
    }
  }

  onCancel($event) {
    $event.preventDefault();
    this.formGroup.reset();
    this.buildFormGroup();
    this.displayForm = false;
  }

  public get allowSubmit() {
    const status = this.formGroup.get('status').value;

    if (status) {
      if (status == this.captureUserAuditOutcomeStatus.compliant) {
        return true;
      } else {
        const selectedOutcomeReasons = this.selectedOutcomeReasons;
        const note = this.formGroup.get('note').value;
        return !isEmpty(selectedOutcomeReasons) && !isEmpty(note);
      }
    } else {
      return false;
    }
  }

  private handleUpdateAuditOutcome(params) {
    this.captureService.updateUserAuditOutcome(this.capture, params).subscribe(
      (capture: Capture) => {
        this.processing = false;
        this.displayForm = false;
        this.captureService.notifyCaptureChanged(capture);
      },
      response => {
        this.processing = false;
        this.serverErrorMessage = response.error.error;
      }
    );
  }

  private handleCreateAuditOutcome(params) {
    this.captureService.createUserAuditOutcome(this.capture, params).subscribe(
      (capture: Capture) => {
        this.processing = false;
        this.displayForm = false;
        this.captureService.notifyCaptureChanged(capture);
      },
      response => {
        this.processing = false;
        this.serverErrorMessage = response.error.error;
      }
    );
  }

  reasonSelected(value): boolean {
    const currentStatusReasons: string[] = this.capture.userAuditOutcome?.statusReasons || [];
    return currentStatusReasons.includes(value);
  }

  public get isNonCompliant(): boolean {
    const status = this.formGroup.get('status').value;
    return (
      status === this.captureUserAuditOutcomeStatus.noncompliant ||
      status === this.captureUserAuditOutcomeStatus.compliantWithMinorFindings
    )
  }

  get selectedOutcomeReasons() {
    return this.availableOutcomeStatusReasons.filter(option => this.formGroup.get(option.value).value)
                                             .map(option => option.value);
  }

  buildFormGroup() {
    this.formGroup = this.formBuilder.group(
      Object.assign({
        status: [this.capture.userAuditOutcome?.status],
        note: [this.capture.userAuditOutcome?.note]
      },
      Object.fromEntries(
        this.availableOutcomeStatusReasons.map(
          reason => [reason.value, new FormControl(this.reasonSelected(reason.value), { nonNullable: true })]
        )
      )
    ));
  }
}
